import React, {useContext, useEffect, useState} from "react";
import {
    homeOverviewGraphUrl,
    homeOverviewUrl,
    DATE_FORMAT
} from "../../../config/generals";
import {AlertTypes, apiClient, IAlertData, MainLoader, setAlert, Grid, Paper, useTheme, Skeleton} from "@leap4/frontend";
import {IHomeOverviewData, advertiserLoginObj} from "../../../interfaces/IOverviewData";
import {HomeGeneralInfo} from "./homeGeneralInfo";
import OverviewGraph, {IOverviewGraphResponse} from "../../common/OverviewGraph";
import {TagsTable} from "./TagsTable";
import {DateTime} from "luxon";
import {TimeNavbarPickerWrapper} from "../../common/TimeNavbarPickerWrapper/TimeNavbarPickerWrapper";
import {connect, ConnectedProps} from "react-redux";
import {RootState} from "../../../interfaces/root-state.interface";
import {useQuery} from "react-query";
import {AdvertiserLoginsContext, AdvertiserLoginsState} from "../../../store/context/AdvertiserLogins";
import {SelectedALContext, SelectedALState} from "../../../store/context/SelectedAL";
import {queryClient} from "../../../index";

export interface IALData {
    aLId: string,
    aLName: string,
    partners: advertiserLoginObj
}

const mapStateToProps = (state: RootState) => ({
    selectedOrganization: state.auth.selectedOrganizationId,
});

const mapActions = {
    setAlert
}

const connector = connect(mapStateToProps, mapActions);

type PropsFromRedux = ConnectedProps<typeof connector>;

export interface IHomeProps extends PropsFromRedux {
    setAlert: (alert: IAlertData) => void
}

export const FETCH_HOME_OVERVIEW='fetch_home_overview'
export const FETCH_HOME_GRAPH='fetch_home_graph'

const _HomePage = (props: IHomeProps): JSX.Element => {
    const {setAlert, selectedOrganization} = props;
    const theme = useTheme()
    const {selectedAL} = useContext<SelectedALState>(SelectedALContext);
    const {
        advertiserLogins,
        isLoadingAL
    } = useContext<AdvertiserLoginsState>(AdvertiserLoginsContext);

    const getAvailablePartners = () :advertiserLoginObj  =>{
        return selectedAL ? advertiserLogins?.find(al => al.aLId === selectedAL)?.partners : {partners: [], toSkipDiscrepancy: false}
    }
    const [availablePartners, setAvailablePartners] = useState<advertiserLoginObj>(getAvailablePartners())
    useEffect(()=>{
        setAvailablePartners(getAvailablePartners())
    },[selectedAL])
    useEffect(()=>{
       setDates(initialStartEndDates())
    },[availablePartners])
    const initialStartEndDates = (): { startDate: string, endDate: string } => {
        const luxonDate = DateTime.fromJSDate(new Date())
        const formatter = (s: DateTime) => s.toUTC().toFormat(DATE_FORMAT)
        if(availablePartners.toSkipDiscrepancy){
            return {
                startDate: formatter(luxonDate),
                endDate: formatter(luxonDate)
            };
        }
        if (luxonDate.hour <= 17) {
            return {
                endDate: formatter(luxonDate.minus({day: 2})),
                startDate: formatter(luxonDate.minus({day: 2}))
            }
        }
        return {
            startDate: formatter(luxonDate.minus({day: 1})),
            endDate: formatter(luxonDate.minus({day: 1}))
        };
    }


    const [dates, setDates] = useState<{ startDate: string, endDate: string }>(initialStartEndDates())


    const fetchPageOverview = async (): Promise<IHomeOverviewData> => {
        if(!selectedAL){
            return Promise.resolve(null)
        }
        return (await apiClient.post(homeOverviewUrl, {
            dates: {
                startDate: dates.startDate, endDate: dates.endDate
            },
            targetOrg: selectedAL
        })).data
    }
    const {
        data: overviewData,
        refetch: refetchPageOverview,
        isFetching: isLoadingPageOverview
    } = useQuery([FETCH_HOME_OVERVIEW, selectedAL, dates, selectedOrganization], fetchPageOverview, {
        refetchOnWindowFocus: false,
        onError: (err: any) => {
            queryClient.setQueryData(FETCH_HOME_OVERVIEW, null)
            setAlert({msg: err.message, type: AlertTypes.FAILURE})
        },
        cacheTime: 0,
        initialData: null,
        retry: 0
    })

    const fetchPageGraph = async (): Promise<IOverviewGraphResponse> => {
        queryClient.setQueryData(FETCH_HOME_GRAPH, null)
        if(!selectedAL){
            return Promise.resolve({data:[]})
        }
        return (await apiClient.post(homeOverviewGraphUrl, {
            dates: {
                startDate: dates.startDate, endDate: dates.endDate
            },
            targetOrg: selectedAL
        })).data

    }
    const {
        data: graphData,
        isFetching: isLoadingGraph,
        refetch: refetchGraph
    } = useQuery([FETCH_HOME_GRAPH, dates, selectedAL, selectedOrganization], fetchPageGraph, {
        refetchOnWindowFocus: false,
        onError: (err: Error) => {
            queryClient.setQueryData(FETCH_HOME_GRAPH, null)
            setAlert({msg: err.message, type: AlertTypes.FAILURE})
        },
        cacheTime: 0,
        initialData: null,
        retry: 0


    })

    const isLoading = isLoadingGraph || isLoadingPageOverview || isLoadingAL;

    if(isLoading || !selectedAL) return (
        <div style={{marginTop: '20vh'}}>
        <MainLoader />
        </div>
    )

    return (

        <>
            <TimeNavbarPickerWrapper dates={dates} setDates={(startDate, endDate) => setDates({startDate, endDate})}
                                     toSkipDiscrepancy={availablePartners.toSkipDiscrepancy}
                                     isLoading={isLoading}/>

            <>
                <Grid container direction={'column'} justifyContent={'center'} alignItems={'center'} gap={2}
                      sx={{ marginTop: '2px' }}>
                    {isLoadingPageOverview ?
                        <Skeleton variant={"rounded"} height={"40px"} width={"96%"}
                                  animation={"wave"} sx={{borderRadius: "16px"}}/>
                        :
                        <Paper elevation={3} sx={{
                                width: '96%',
                                borderRadius: '16px',
                                py: 0.5,
                                filter: theme?.palette?.mode === 'dark' ? 'drop-shadow(0px 0px 2px lightgray)' : 'drop-shadow(0px 0px 5px lightgray)',
                            }}>
                            <HomeGeneralInfo data={overviewData?.overviewTotals?.totals}/>
                        </Paper>
                    }

                    <Grid sx={{height: '400px', margin: '0 auto', width: "96%"}}
                          style={{filter: theme?.palette?.mode === 'dark' ? 'drop-shadow(0px 0px 2px lightgray' : 'drop-shadow(0px 0px 5px lightgray'}}>

                        <OverviewGraph isLoading={isLoadingGraph}
                                       graphData={graphData}
                                       startDate={dates.startDate.toString()}
                                       endDate={dates.endDate.toString()}
                        />
                    </Grid>

                    {isLoading ?
                        <Skeleton variant={"rounded"} height={"150px"} width={"96%"}
                                  animation={"wave"} sx={{borderRadius: "16px"}}/>
                        :
                        <Grid width={"96%"}>
                        <TagsTable loading={!!!selectedAL && selectedAL != ''} refreshFunc={refetchPageOverview}
                                   tagsData={overviewData?.overviewTotals?.tagsData || []}
                        />
                        </Grid>
                    }

                </Grid>

            </>
        </>
    );

}
export const HomePage = connector(_HomePage)


